/**
 * Emotion-backed shim mimicking the `@stitches/react` styled API that this codebase
 * was built on. Preserves the call shape `styled('tag', { ...css, variants, defaultVariants })`
 * so consumers don't need to change. Resolves stitches-style `$token` references and
 * applies variant CSS based on component props.
 *
 * Migrated to remove the abandoned `@stitches/react` dep (Phase 2c). New code should
 * prefer `@emotion/styled` or MUI `styled` directly.
 */
import type { CSSObject } from '@emotion/react';
import type { StyledComponent } from '@emotion/styled';
import emotionStyled from '@emotion/styled';
import type { ComponentProps, ComponentType, ElementType, JSX } from 'react';
import breakpoints from './breakpoints';
import colors from './colors';
import truentityColors from './truentityColors';

const mediaAliases: Record<string, string> = breakpoints;

const fontSizes: Record<string, string> = {
  sm: '0.75rem',
  md: '0.875rem',
  lg: '1rem',
  xl: '1.25rem',
  xxl: '1.5rem',
  xxxl: '2rem'
};

const fontWeights: Record<string, string> = {
  light: '300',
  normal: '400',
  medium: '500',
  semi: '600',
  bold: '700'
};

const radii: Record<string, string> = {
  none: '0px',
  sm: '2px',
  md: '3px',
  lg: '4px',
  xl: '6px',
  round: '9999px'
};

const allColors: Record<string, string> = { ...(colors as Record<string, string>), ...(truentityColors as Record<string, string>) };

const tokenScopes: Record<string, Record<string, string>> = {
  colors: allColors,
  fontSizes,
  fontWeights,
  radii
};

// Look up either `$scope$key` (explicit scope) or bare `$key` (search all scopes).
function resolveTokenRef(scope: string | undefined, key: string): string | undefined {
  if (scope) return tokenScopes[scope]?.[key];
  for (const scopeMap of Object.values(tokenScopes)) {
    if (scopeMap[key] !== undefined) return scopeMap[key];
  }
  return undefined;
}

function resolveTokens(value: string): string {
  return value
    .replace(/\$([a-zA-Z]+)\$([a-zA-Z][a-zA-Z0-9]*)/g, (m, scope, key) => resolveTokenRef(scope, key) ?? m)
    .replace(/\$([a-zA-Z][a-zA-Z0-9]*)/g, (m, key) => resolveTokenRef(undefined, key) ?? m);
}

function resolveCss(obj: unknown, omitVariantKeys = false): unknown {
  if (typeof obj === 'string') return resolveTokens(obj);
  if (Array.isArray(obj)) return obj.map(o => resolveCss(o));
  if (obj && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (omitVariantKeys && (k === 'variants' || k === 'defaultVariants')) continue;
      const alias = k.startsWith('@') ? mediaAliases[k.slice(1)] : undefined;
      result[alias ? `@media ${alias}` : k] = resolveCss(v);
    }
    return result;
  }
  return obj;
}

function applyVariants(
  props: Record<string, unknown>,
  variants: Record<string, Record<string, unknown>> | undefined,
  defaultVariants: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!variants) return {};
  let css: Record<string, unknown> = {};
  for (const [name, options] of Object.entries(variants)) {
    let value = props[name];
    if (value === undefined) value = defaultVariants?.[name];
    if (value === undefined) continue;
    const optionCss = options[String(value)];
    if (optionCss) {
      css = { ...css, ...(resolveCss(optionCss) as Record<string, unknown>) };
    }
  }
  return css;
}

type StyledOptions = Record<string, unknown> & {
  variants?: Record<string, Record<string, unknown>>;
  defaultVariants?: Record<string, unknown>;
};

/**
 * Stitches-shaped variant-prop type extractor. The original
 * `StitchesVariants<typeof Component>` extracted variant prop types from the styled
 * component itself; we can't reproduce that with emotion at the type level without a
 * lot of generics gymnastics, so consumers that need the variant prop types should
 * declare them manually. The export is preserved for backwards compatibility but
 * resolves to a permissive map.
 *
 * The type parameter is ignored on purpose (see above) but has to stay, so that the ~30 existing
 * `StitchesVariants<typeof X>` callsites keep compiling.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type StitchesVariants<_C> = Record<string, string | boolean | undefined>;

// Backwards-compat for `truentityTheme` — used in App.tsx as a className.
// In stitches it returned a generated className that scoped the theme tokens; emotion
// inlines token values at build time, so we no longer need a className wrapper.
export const truentityTheme = '';

export { default as colors } from './colors';
export { theme as muiTheme } from './mui-theme';

/**
 * Overloaded to mirror emotion's own split between intrinsic tags and components. A single signature
 * taking the whole `ElementType` union made TypeScript resolve emotion's *ComponentClass* overload
 * for every call, so `styled('input', …)` handed back a component whose `ref` wanted a
 * `Component` rather than an `HTMLInputElement`. Splitting it keeps the concrete element type, and
 * the `Record<string, unknown>` intersection is what lets variant props through.
 */
type StyledFn = {
  <Tag extends keyof JSX.IntrinsicElements>(tag: Tag, cssObj: StyledOptions): StyledComponent<
    JSX.IntrinsicElements[Tag] & Record<string, unknown>
  >;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ComponentProps<C> requires C to be
  // a real ElementType; `ComponentType<unknown>`/`<never>` do not satisfy that constraint.
  <C extends ComponentType<any>>(tag: C, cssObj: StyledOptions): StyledComponent<ComponentProps<C> & Record<string, unknown>>;
};

// Declared as one `const` with an overloaded *type* rather than TS overload signatures: eslint's base
// `no-redeclare` rule is not overload-aware and reports each signature as a duplicate `styled`.
const styled: StyledFn = (tag: ElementType, cssObj: StyledOptions) => {
  const baseCss = resolveCss(cssObj, true) as CSSObject;
  const variants = cssObj.variants;
  const defaultVariants = cssObj.defaultVariants;
  const variantPropNames = variants ? Object.keys(variants) : [];

  // The implementation stays deliberately loose -- `tag` is only known concretely at the overloads
  // above, and emotion has no signature accepting the bare union.
  const create = emotionStyled(tag as 'div', {
    // Don't forward variant props to the DOM
    shouldForwardProp: (prop: string) => !variantPropNames.includes(prop)
  });

  return create(
    // Annotated CSSObject: otherwise the spread object infers `{ [x: string]: unknown }`, which
    // emotion's Interpolation union rejects.
    (props: Record<string, unknown>): CSSObject => ({
      ...baseCss,
      ...(applyVariants(props, variants, defaultVariants) as CSSObject)
    })
  );
};

export default styled;
